/**
 * GDPR / ePrivacy cookie consent banner.
 *
 * Three controls:
 *   • Accept all  — sets analytics + error_monitoring = true
 *   • Reject all  — sets both to false
 *   • Customize   — granular toggles in an expanded panel
 *
 * Strictly necessary cookies (auth, language, this consent record) are not
 * listed here because we cannot legally make them optional — they are
 * required for the Service to work.
 *
 * The choice persists in localStorage under `rentnova_consent` so we only
 * ask once. The banner is hidden inside /app/* (post-auth) to avoid
 * cluttering the product UI; we revisit consent there via Settings → Privacy.
 *
 * Dispatching `window.rentnovaConsent` lets future scripts (analytics,
 * Sentry) read the current choice without re-importing this module.
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const STORAGE_KEY = 'rentnova_consent';
const VERSION = 1;

type Preferences = {
  version: number;
  decided_at: string;
  analytics: boolean;
  error_monitoring: boolean;
};

type ConsentEvent = CustomEvent<Preferences>;

declare global {
  interface Window {
    rentnovaConsent?: Preferences;
  }
}

function readStoredConsent(): Preferences | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Preferences;
    if (parsed?.version !== VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function persist(prefs: Preferences) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    window.rentnovaConsent = prefs;
    window.dispatchEvent(new CustomEvent<Preferences>('rentnova:consent', { detail: prefs }));
  } catch {
    // localStorage may be blocked (Safari private). Banner re-appears next visit; acceptable.
  }
}

export default function CookieBanner() {
  const { pathname } = useLocation();
  const insideApp = pathname.startsWith('/app');

  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [errorMonitoring, setErrorMonitoring] = useState(false);

  useEffect(() => {
    const stored = readStoredConsent();
    if (stored) {
      window.rentnovaConsent = stored;
      setAnalytics(stored.analytics);
      setErrorMonitoring(stored.error_monitoring);
      return;
    }
    if (insideApp) return; // do not surface banner mid-task in product UI
    setVisible(true);
  }, [insideApp]);

  if (!visible) return null;

  const decide = (a: boolean, e: boolean) => {
    const prefs: Preferences = {
      version: VERSION,
      decided_at: new Date().toISOString(),
      analytics: a,
      error_monitoring: e,
    };
    persist(prefs);
    setVisible(false);
  };

  return (
    <div role="dialog" aria-label="Cookie consent" style={wrapper}>
      <div style={card}>
        <div>
          <h3 style={title}>We use cookies</h3>
          <p style={body}>
            We use strictly necessary cookies to run RentNova (signing you in,
            remembering your language). With your permission we'd also enable
            privacy-friendly analytics and error monitoring so we can keep the
            app fast and bug-free. You can change this anytime in{' '}
            <a href="/cookies" style={link}>Cookie Policy</a> or your browser settings.
          </p>

          {expanded && (
            <div style={optionsBlock}>
              <Toggle
                label="Analytics"
                description="Plausible Analytics (aggregate page views, no profiling)."
                checked={analytics}
                onChange={setAnalytics}
              />
              <Toggle
                label="Error monitoring"
                description="Sentry (captures runtime errors so we can fix bugs)."
                checked={errorMonitoring}
                onChange={setErrorMonitoring}
              />
            </div>
          )}
        </div>

        <div style={buttonsRow}>
          {!expanded && (
            <button type="button" onClick={() => setExpanded(true)} style={ghostBtn}>
              Customize
            </button>
          )}
          {expanded && (
            <button
              type="button"
              onClick={() => decide(analytics, errorMonitoring)}
              style={ghostBtn}
            >
              Save preferences
            </button>
          )}
          <button type="button" onClick={() => decide(false, false)} style={secondaryBtn}>
            Reject all
          </button>
          <button type="button" onClick={() => decide(true, true)} style={primaryBtn}>
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}

function Toggle(props: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label style={toggleRow}>
      <input
        type="checkbox"
        checked={props.checked}
        onChange={(e) => props.onChange(e.target.checked)}
        style={{ marginTop: 4 }}
      />
      <span>
        <span style={{ fontWeight: 600, color: '#0f172a' }}>{props.label}</span>
        <span style={{ display: 'block', fontSize: '0.8125rem', color: '#64748b' }}>
          {props.description}
        </span>
      </span>
    </label>
  );
}

// ─ styles (kept inline so this file works without a CSS import dependency) ─

const wrapper: React.CSSProperties = {
  position: 'fixed',
  left: '1rem',
  right: '1rem',
  bottom: '1rem',
  zIndex: 9999,
  display: 'flex',
  justifyContent: 'center',
  pointerEvents: 'none',
};

const card: React.CSSProperties = {
  pointerEvents: 'auto',
  maxWidth: 720,
  width: '100%',
  background: '#ffffff',
  borderRadius: 16,
  border: '1px solid #e2e8f0',
  boxShadow: '0 20px 60px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.06)',
  padding: '1.25rem 1.5rem',
};

const title: React.CSSProperties = {
  margin: 0,
  fontSize: '1.0625rem',
  fontWeight: 700,
  color: '#0f172a',
};

const body: React.CSSProperties = {
  margin: '0.5rem 0 0',
  fontSize: '0.9375rem',
  lineHeight: 1.55,
  color: '#334155',
};

const link: React.CSSProperties = {
  color: '#047857',
  fontWeight: 600,
  textDecoration: 'underline',
};

const optionsBlock: React.CSSProperties = {
  marginTop: '1rem',
  padding: '0.875rem 1rem',
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  background: '#f8fafc',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
};

const toggleRow: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gap: '0.625rem',
  alignItems: 'flex-start',
  cursor: 'pointer',
};

const buttonsRow: React.CSSProperties = {
  marginTop: '1.125rem',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.5rem',
  justifyContent: 'flex-end',
};

const buttonBase: React.CSSProperties = {
  padding: '0.5rem 1rem',
  borderRadius: 10,
  fontSize: '0.875rem',
  fontWeight: 600,
  cursor: 'pointer',
  border: '1px solid transparent',
};

const ghostBtn: React.CSSProperties = {
  ...buttonBase,
  background: 'transparent',
  borderColor: '#e2e8f0',
  color: '#475569',
};

const secondaryBtn: React.CSSProperties = {
  ...buttonBase,
  background: '#f1f5f9',
  color: '#0f172a',
};

const primaryBtn: React.CSSProperties = {
  ...buttonBase,
  background: '#047857',
  color: '#ffffff',
};

// Helper: listen for consent changes elsewhere in the app.
export function onConsentChange(cb: (prefs: Preferences) => void) {
  const handler = (e: Event) => cb((e as ConsentEvent).detail);
  window.addEventListener('rentnova:consent', handler);
  return () => window.removeEventListener('rentnova:consent', handler);
}

export function getConsent(): Preferences | null {
  return window.rentnovaConsent ?? readStoredConsent();
}
